import { entityDef, expansionCost, parcelBalance } from '../config/balancing';
import { ATTRACTION_LADDER, catalogItem } from '../config/catalog';
import { PARCELS, parcelById } from '../config/map';
import {
  hasMacroEntitlement,
  landOwnsPlot,
  plotById,
  plotLabel,
  remainingMacroEntitlementCost,
} from '../spatial/plots';
import { catalogLandEntitlement } from './landGuidance';
import type { EntityKind, ParkState } from '../types';
import { computeParkScore } from './economy';

export interface UnlockResult {
  ok: boolean;
  reason: string;
}

/** Current/live existence only. */
export function hasKind(state: ParkState, kind: EntityKind): boolean {
  return state.entities.some((entity) => entity.kind === kind);
}

/** Permanent build prerequisite: compatible with old saves by accepting a currently live entity too. */
export function hasBuiltKind(state: ParkState, kind: EntityKind): boolean {
  return hasKind(state, kind) || state.builtKindMilestones?.includes(kind) === true;
}

export function catalogNeedsLandGuidance(kind: EntityKind, state: ParkState): boolean {
  return catalogLandEntitlement(kind, state.unlockedLandIds) != null;
}

export function evaluateCatalogUnlock(kind: EntityKind, state: ParkState): UnlockResult {
  const spec = entityDef(kind).unlock;
  if (spec.requiresKind && !hasBuiltKind(state, spec.requiresKind)) {
    return { ok: false, reason: `Need ${catalogItem(spec.requiresKind).name}` };
  }
  if (spec.requiresParcel && !hasMacroEntitlement(state.unlockedLandIds, spec.requiresParcel)) {
    const parcel = parcelById(spec.requiresParcel);
    return { ok: false, reason: `Need ${parcel?.label ?? spec.requiresParcel}` };
  }
  if (spec.minParkScore !== undefined && computeParkScore(state) < spec.minParkScore) {
    return { ok: false, reason: `Park ${spec.minParkScore}` };
  }
  return { ok: true, reason: '' };
}

export function evaluateParcelUnlock(parcelId: string, state: ParkState): UnlockResult {
  const plot = plotById(parcelId);
  if (plot) {
    if (plot.startsUnlocked || landOwnsPlot(state.unlockedLandIds, parcelId)) {
      return { ok: false, reason: 'Already unlocked' };
    }
    return { ok: true, reason: '' };
  }
  const parcel = parcelById(parcelId);
  const spec = parcelBalance(parcelId);
  if (!parcel || !spec) {
    return { ok: false, reason: 'Unknown land' };
  }
  if (parcel.startsUnlocked || hasMacroEntitlement(state.unlockedLandIds, parcelId)) {
    return { ok: false, reason: 'Already unlocked' };
  }
  if (spec.requiresParcel && !hasMacroEntitlement(state.unlockedLandIds, spec.requiresParcel)) {
    const required = parcelById(spec.requiresParcel);
    return { ok: false, reason: `Need ${required?.label ?? spec.requiresParcel} first` };
  }
  return { ok: true, reason: '' };
}

export function nextGoal(state: ParkState): { label: string; detail: string } {
  for (const kind of ATTRACTION_LADDER) {
    if (hasBuiltKind(state, kind)) {
      continue;
    }
    const item = catalogItem(kind);
    const spec = entityDef(kind).unlock;
    const label = `Next: ${item.name}`;
    if (spec.requiresKind && !hasBuiltKind(state, spec.requiresKind)) {
      return { label, detail: `Need ${catalogItem(spec.requiresKind).name}` };
    }
    if (spec.requiresParcel && !hasMacroEntitlement(state.unlockedLandIds, spec.requiresParcel)) {
      const parcel = parcelById(spec.requiresParcel);
      const remaining = remainingMacroEntitlementCost(state.unlockedLandIds, spec.requiresParcel);
      const cost = remaining > 0 ? remaining : expansionCost(spec.requiresParcel);
      return { label, detail: `Unlock ${parcel?.label ?? spec.requiresParcel} · ${state.coins}/${cost} coins` };
    }
    if (spec.minParkScore !== undefined) {
      const score = computeParkScore(state);
      if (score < spec.minParkScore) {
        return { label, detail: `Park ${score}/${spec.minParkScore}` };
      }
    }
    const cost = entityDef(kind).purchaseCost;
    if (state.coins < cost) {
      return { label, detail: `${state.coins}/${cost} coins` };
    }
    return { label, detail: `Ready · ${cost} coins` };
  }
  return { label: 'Coaster built', detail: 'Keep growing the park' };
}

export function lockedParcelLabel(parcelId: string): string {
  const plot = plotById(parcelId);
  const cost = expansionCost(parcelId);
  if (plot) {
    return `LOCKED\n${plotLabel(parcelId)}\n${cost}`;
  }
  const parcel = parcelById(parcelId);
  return `LOCKED\n${parcel?.label ?? parcelId}\n${cost}`;
}

export function purchasableParcels(): string[] {
  return PARCELS.filter((parcel) => !parcel.startsUnlocked).map((parcel) => parcel.id);
}
